import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, MapPin, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { API, authHeaders } from "./adminApi";
import { mergeCommunes, resetCommunesCache } from "../../lib/useCommunes";

export default function CommunesAdmin({ token, onUnauthorized }) {
  const [list, setList] = useState(null);
  const [draft, setDraft] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/communes`).then((r) => setList(mergeCommunes(r.data)));
  useEffect(() => { load(); }, []);

  const handleError = (e, fallback) => {
    if (e.response?.status === 401) return onUnauthorized();
    toast.error(e.response?.data?.detail || fallback);
  };

  const refresh = () => {
    resetCommunesCache();
    return load();
  };

  const save = async () => {
    setSaving(true);
    try {
      if (draft.key) {
        await axios.put(`${API}/admin/communes/${draft.key}`, { name: draft.name, text: draft.text, hidden: draft.hidden, builtin: draft.builtin }, { headers: authHeaders(token) });
        toast.success("Commune mise à jour");
      } else {
        const r = await axios.post(`${API}/admin/communes`, { name: draft.name, text: draft.text }, { headers: authHeaders(token) });
        toast.success(`${r.data.name} ajoutée sur la carte`);
      }
      setOpen(false);
      await refresh();
    } catch (e) {
      handleError(e, "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const toggleHidden = async (c) => {
    try {
      await axios.put(`${API}/admin/communes/${c.key}`, { name: c.name, text: c.text, hidden: !c.hidden, builtin: c.builtin }, { headers: authHeaders(token) });
      await refresh();
    } catch (e) {
      handleError(e, "Erreur");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Retirer ${c.name} de la carte ?`)) return;
    try {
      await axios.delete(`${API}/admin/communes/${c.key}`, { headers: authHeaders(token) });
      toast.success(`${c.name} retirée`);
      await refresh();
    } catch (e) {
      handleError(e, "Erreur lors de la suppression");
    }
  };

  const edit = (c) => {
    setDraft(c);
    setOpen(true);
  };

  return (
    <div data-testid="admin-communes-page" className="space-y-6">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-semibold text-[#1A2E1C]">Communes</h2>
          <p className="text-[#5C705E] mt-1">Carte et listes de la page « Zone d'intervention ». La position est trouvée automatiquement.</p>
        </div>
        <Button onClick={() => edit({ name: "", text: "" })} data-testid="commune-create" className="rounded-full bg-[#1B5E20] hover:bg-[#164d1a] text-white">
          <Plus className="w-4 h-4 mr-2" /> Ajouter une commune
        </Button>
      </header>

      {!list ? (
        <div className="text-center py-20 text-[#5C705E]">Chargement…</div>
      ) : (
        <div className="bg-white border border-[#E5EBE5] rounded-2xl divide-y divide-[#E5EBE5]" data-testid="communes-admin-list">
          {list.map((c) => (
            <div key={c.key} data-testid={`commune-row-${c.key}`} className={`p-4 flex items-start gap-4 ${c.hidden ? "opacity-50" : ""}`}>
              <MapPin className={`w-5 h-5 mt-0.5 flex-shrink-0 ${c.builtin ? "text-[#1B5E20]" : "text-[#66BB6A]"}`} />
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-[#1A2E1C]">{c.name}</span>
                  <span className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F1F8F2] text-[#1B5E20]">
                    {c.builtin ? "Page dédiée" : "Ajoutée"}
                  </span>
                  {c.hidden && <span className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-[#7B5300]">Masquée</span>}
                  {c.href && (
                    <a href={c.href} target="_blank" rel="noreferrer" className="text-[#5C705E] hover:text-[#1B5E20]"><ExternalLink className="w-3.5 h-3.5" /></a>
                  )}
                </div>
                <p className="text-sm text-[#5C705E] mt-1 line-clamp-2">
                  {c.text || (c.builtin ? "Texte d'origine de la page." : "Aucun texte.")}
                </p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => edit({ ...c })} data-testid={`commune-edit-${c.key}`} title="Modifier le texte" className="p-2 rounded-full hover:bg-[#E8F5E9]">
                  <Pencil className="w-4 h-4 text-[#1B5E20]" />
                </button>
                <button onClick={() => toggleHidden(c)} data-testid={`commune-toggle-${c.key}`} title={c.hidden ? "Afficher" : "Masquer"} className="p-2 rounded-full hover:bg-[#E8F5E9]">
                  {c.hidden ? <Eye className="w-4 h-4 text-[#1B5E20]" /> : <EyeOff className="w-4 h-4 text-[#5C705E]" />}
                </button>
                {!c.builtin && (
                  <button onClick={() => remove(c)} data-testid={`commune-delete-${c.key}`} title="Supprimer" className="p-2 rounded-full hover:bg-red-50">
                    <Trash2 className="w-4 h-4 text-[#D32F2F]/80" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby="commune-form-desc" className="max-w-xl rounded-3xl p-6 border-[#E5EBE5]">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-2xl font-semibold text-[#1A2E1C]">
              {draft?.key ? draft.name : "Nouvelle commune"}
            </DialogTitle>
            <p id="commune-form-desc" className="text-sm text-[#5C705E]">
              {draft?.builtin
                ? "Ce texte remplace le premier paragraphe de la page de la commune et s'affiche sur la carte. Laissez vide pour garder le texte d'origine."
                : "Le texte s'affiche dans la fiche de la commune sur la carte."}
            </p>
          </DialogHeader>
          {draft && (
            <div className="space-y-4 mt-4">
              {!draft.key && (
                <div>
                  <Label className="mb-1.5 block">Nom de la commune (Creuse)</Label>
                  <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="ex : Chénérailles" data-testid="commune-name-input" className="rounded-xl h-11" />
                </div>
              )}
              <div>
                <Label className="mb-1.5 block">Texte des interventions</Label>
                <Textarea value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} rows={5} maxLength={1200} placeholder="ex : Nous intervenons régulièrement à … pour les nids de frelons, les rongeurs…" data-testid="commune-text-input" className="rounded-xl" />
              </div>
            </div>
          )}
          <div className="mt-6 flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Annuler</Button>
            <Button onClick={save} disabled={saving || !draft?.name?.trim()} data-testid="commune-save" className="rounded-full bg-[#1B5E20] hover:bg-[#164d1a] text-white">
              {saving ? "Recherche…" : "Enregistrer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
